import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Check, ChevronDown, X } from 'lucide-react';

interface MultiSelectChipsOption<T extends string> {
  value: T;
  label: string;
}

interface MultiSelectChipsProps<T extends string> {
  value: T[];
  options: MultiSelectChipsOption<T>[];
  onChange: (next: T[]) => void;
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
  className?: string;
  menuPosition?: 'down' | 'up';
}

export function MultiSelectChips<T extends string>({
  value,
  options,
  onChange,
  placeholder,
  id,
  ariaLabel,
  className,
  menuPosition = 'down',
}: MultiSelectChipsProps<T>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (val: T) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const remove = (val: T) => onChange(value.filter((v) => v !== val));
  const clearAll = () => onChange([]);

  const labelFor = (val: T) =>
    options.find((o) => o.value === val)?.label ?? String(val);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex min-h-[2.25rem] w-full items-center justify-between gap-2 rounded-lg border bg-transparent px-2 py-1.5 text-left text-sm transition-colors',
          'border-slate-300 hover:border-blue-300 focus-visible:border-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30',
          'dark:border-gray-600 dark:bg-gray-800 dark:hover:border-blue-500/60'
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {value.length === 0 ? (
            <span className="text-title-page-color-300/70 dark:text-gray-400">
              {placeholder}
            </span>
          ) : (
            value.map((val) => (
              <span
                key={val}
                className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-200"
              >
                {labelFor(val)}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(val);
                  }}
                  className="-mr-1 rounded p-0.5 hover:bg-blue-200/70 dark:hover:bg-blue-400/30"
                  aria-label={`Remove ${labelFor(val)}`}
                >
                  <X size={10} />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 text-slate-400">
          {value.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              className="rounded p-0.5 hover:text-red-600 dark:hover:text-red-400"
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={cn('transition-transform', open && 'rotate-180')}
          />
        </div>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-multiselectable="true"
          className={cn(
            'absolute z-30 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black/5',
            'dark:border-gray-700 dark:bg-gray-900 dark:ring-white/10',
            menuPosition === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {options.map((opt) => {
            const checked = value.includes(opt.value);
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={checked}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 transition-colors',
                  checked
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-200'
                    : 'text-title-page-color-100 hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-gray-700/60'
                )}
                onClick={() => toggle(opt.value)}
              >
                <span className="truncate">{opt.label}</span>
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    checked
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-slate-300 dark:border-gray-600'
                  )}
                >
                  {checked && <Check size={12} />}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
